export const TALENT_REVIEW_GRID_KEY = 'grid';
export const TALENT_REVIEW_ROLE_KEY = 'role';
export const TALENT_REVIEW_DEPARTMENTS_KEY = 'departments';

export type DepartmentToken = {
	id: string;
	recursive: boolean;
};

export function parseDepartmentsParam(value: string | null): DepartmentToken[] | undefined {
	if (value === null) {
		return undefined;
	}

	if (value.trim() === '') {
		return [];
	}

	return value.split(',').flatMap((token) => {
		const trimmed = token.trim();
		if (!trimmed) {
			return [];
		}

		const recursive = trimmed.endsWith('+');
		const id = recursive ? trimmed.slice(0, -1) : trimmed;
		if (!id) {
			return [];
		}

		return [{ id, recursive }];
	});
}

export function serializeDepartmentsParam(tokens: DepartmentToken[]): string | undefined {
	if (tokens.length === 0) {
		return undefined;
	}

	return tokens.map((token) => token.id + (token.recursive ? '+' : '')).join(',');
}

export function matchDepartmentTokens<Group extends { id: string }>(
	tokens: DepartmentToken[],
	groups: Group[],
): Array<{ group: Group; recursive: boolean }> {
	return tokens.flatMap((token) => {
		const group = groups.find((match) => match.id === token.id);
		return group ? [{ group, recursive: token.recursive }] : [];
	});
}

export function parseRoleParam(
	value: string | null,
	allowed: readonly string[],
): string | undefined {
	if (!value) {
		return undefined;
	}

	return allowed.includes(value) ? value : undefined;
}

export function resolveTemplateID(
	templateID: string | undefined,
	templateIDs: readonly string[],
): string | undefined {
	if (templateIDs.length === 0) {
		return templateID;
	}

	if (templateID && templateIDs.includes(templateID)) {
		return templateID;
	}

	return templateIDs[0];
}

export function parseTalentReviewSearch(
	search: string,
	allowedRoles: readonly string[],
): {
	grid: string | undefined;
	role: string | undefined;
	departments: DepartmentToken[] | undefined;
} {
	const params = new URLSearchParams(search.replace(/^\?/, ''));

	return {
		grid: params.get(TALENT_REVIEW_GRID_KEY) ?? undefined,
		role: parseRoleParam(params.get(TALENT_REVIEW_ROLE_KEY), allowedRoles),
		departments: parseDepartmentsParam(params.get(TALENT_REVIEW_DEPARTMENTS_KEY)),
	};
}

export function applyTalentReviewSearch(
	search: string,
	next: {
		grid?: string;
		role?: string;
		departments?: string;
	},
): string {
	const params = new URLSearchParams(search.replace(/^\?/, ''));

	setOrDelete(params, TALENT_REVIEW_GRID_KEY, next.grid);
	setOrDelete(params, TALENT_REVIEW_ROLE_KEY, next.role);
	setOrDelete(params, TALENT_REVIEW_DEPARTMENTS_KEY, next.departments);

	return params.toString();
}

const setOrDelete = (params: URLSearchParams, key: string, value: string | undefined) => {
	if (value) {
		params.set(key, value);
		return;
	}

	params.delete(key);
};
